import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'

import { downloadTaxForm } from '@/domains/Contractor/api'
import { useErrorToast } from '@/hooks/useErrorToast'
import { getOnboardingAddress } from '@/lib/address'
import { downloadFile, formatTaxForm } from '@/lib/utils'
import { CountryName, Widget } from '@/shared/components'
import { Document } from '@/shared/icons/solid'
import { Button, Details } from '@/shared/ui'

import { ContractorOnboardingState } from '../types'

type Props = {
  contractorState?: ContractorOnboardingState
}

export const PersonalInformationReview = ({ contractorState }: Props) => {
  const intl = useIntl()
  const notifyError = useErrorToast()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: downloadTaxForm,
  })

  const handleDownloadTaxForm = async () => {
    if (!contractorState?.taxFormDetails?.taxFormId) return

    try {
      const response = await mutateAsync({
        taxFormId: contractorState.taxFormDetails.taxFormId,
      })

      downloadFile(
        response.data,
        `${contractorState.taxFormDetails.taxFormType}.pdf`,
      )
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <>
      <Widget
        title={
          <FormattedMessage
            defaultMessage="Personal information"
            id="label.personalInformation"
          />
        }
      >
        <Details>
          <Details.Label>
            <FormattedMessage defaultMessage="Birthday" id="label.birthday" />
          </Details.Label>

          <Details.Value>
            {intl.formatDate(
              contractorState?.personalDetails?.dateOfBirth ?? '',
              {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              },
            )}
          </Details.Value>
        </Details>
        <Details>
          <Details.Label>
            <FormattedMessage
              defaultMessage="Country of citizenship"
              id="label.countryOfCitizenship"
            />
          </Details.Label>

          <CountryName
            countryCode={contractorState?.personalDetails?.countryOfCitizenship}
          />
        </Details>
        <Details>
          <Details.Label>
            <FormattedMessage id="label.address" defaultMessage="Address" />
          </Details.Label>

          <Details.Value className="text-right whitespace-break-spaces">
            {contractorState?.personalDetails?.residentialAddress
              ? getOnboardingAddress(
                  contractorState?.personalDetails?.residentialAddress,
                  intl,
                )
              : '-'}
          </Details.Value>
        </Details>
      </Widget>

      <Widget
        title={
          <FormattedMessage
            defaultMessage="Tax information"
            id="label.taxInformation"
          />
        }
      >
        <Details>
          <Details.Label>
            <FormattedMessage
              defaultMessage="Tax residence"
              id="label.taxResidence"
            />
          </Details.Label>

          <CountryName
            countryCode={
              contractorState?.personalDetails?.countryOfTaxResidence
            }
          />
        </Details>
        <Details>
          <Details.Label>
            <FormattedMessage
              defaultMessage="Tax identification number"
              id="label.taxIdentificationNumber"
            />
          </Details.Label>

          <Details.Value>
            {contractorState?.personalDetails?.taxId}
          </Details.Value>
        </Details>

        <Details>
          <Details.Label>
            <FormattedMessage defaultMessage="Tax form" id="label.taxForm" />
          </Details.Label>

          <Button
            variant="ghost"
            size="inline"
            className="underline"
            onClick={handleDownloadTaxForm}
            disabled={isPending}
            loaderPosition="left"
            loading={isPending}
            leftIcon={<Document className="size-4" />}
          >
            {formatTaxForm(contractorState?.taxFormDetails?.taxFormType)}
          </Button>
        </Details>
      </Widget>
    </>
  )
}
